import { useEffect, useState } from 'react';
import { getTenantFlag } from '@/api/feature-flag-api';

const FIRM_DATA_FLAG_SLUG = 'firm-data-new-firms';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const isEnabledValue = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'enabled' || normalized === 'true';
  }
  if (isRecord(value)) {
    return isEnabledValue(value.enabled);
  }
  return false;
};

// Dev-only escape hatch: in a dev build only, `localStorage.forceFirmDataEnabled
// = "true"` forces enabled so the new UI can be previewed without the backend
// flag. Never honored in production builds.
const isDevForceFlagEnabled = (storageKey: string): boolean => {
  try {
    return (
      import.meta.env?.DEV === true &&
      typeof localStorage !== 'undefined' &&
      localStorage.getItem(storageKey) === 'true'
    );
  } catch {
    return false;
  }
};

// Resolved flag state carries the firmId it was resolved for, so a stale result
// from a previous firm can never be reported for the current one.
type ResolvedTenantFlag = {
  firmId: string | undefined;
  enabled: boolean;
  loading: boolean;
};

const useTenantFlagEnabled = (
  flagSlug: string,
  firmId: string | undefined,
  devForceStorageKey?: string,
): { enabled: boolean; loading: boolean } => {
  const [resolved, setResolved] = useState<ResolvedTenantFlag>({
    firmId,
    enabled: false,
    loading: Boolean(firmId),
  });

  useEffect(() => {
    if (devForceStorageKey && isDevForceFlagEnabled(devForceStorageKey)) {
      setResolved({ firmId, enabled: true, loading: false });
      return;
    }

    if (!firmId) {
      setResolved({ firmId, enabled: false, loading: false });
      return;
    }

    let cancelled = false;
    setResolved({ firmId, enabled: false, loading: true });

    getTenantFlag(flagSlug, firmId)
      .then((flag) => {
        if (!cancelled) {
          setResolved({ firmId, enabled: isEnabledValue(flag?.value), loading: false });
        }
      })
      .catch(() => {
        // Missing flag / failed lookup → treat as disabled (legacy behavior).
        if (!cancelled) setResolved({ firmId, enabled: false, loading: false });
      });

    return () => {
      cancelled = true;
    };
  }, [devForceStorageKey, firmId, flagSlug]);

  // If firmId changed since the last resolution, the stored enabled/loading
  // belong to a different firm — report disabled + loading until this firm
  // resolves, so a previously-enabled firm never leaks its state to a new one.
  if (resolved.firmId !== firmId) {
    return { enabled: false, loading: Boolean(firmId) };
  }

  return { enabled: resolved.enabled, loading: resolved.loading };
};

export const useFirmDataEnabled = (
  firmId: string | undefined,
): { enabled: boolean; loading: boolean } =>
  useTenantFlagEnabled(FIRM_DATA_FLAG_SLUG, firmId, 'forceFirmDataEnabled');

// Fail closed until read APIs and production source verification are ready.
export const useCostBasisEnabled = (firmId: string | undefined) =>
  useTenantFlagEnabled('investor-cost-basis', firmId);

// Non-hook counterpart of useFirmDataEnabled for callers outside React
// (e.g. Redux thunks) that need the same firm-data-enabled signal without a
// component lifecycle. Mirrors the hook's resolution logic exactly so both
// stay backed by the same tenant flag.
export const resolveFirmDataEnabled = async (firmId: string | undefined): Promise<boolean> => {
  if (isDevForceFlagEnabled('forceFirmDataEnabled')) {
    return true;
  }
  if (!firmId) {
    return false;
  }
  try {
    const flag = await getTenantFlag(FIRM_DATA_FLAG_SLUG, firmId);
    return isEnabledValue(flag?.value);
  } catch {
    // Missing flag / failed lookup → treat as disabled (legacy behavior).
    return false;
  }
};

export default useFirmDataEnabled;
